import React from 'react';
import { useTranslation } from 'react-i18next';

import loadable from '@loadable/component';

import {
  FundraisingPageFragment,
  PageStatus,
} from '../../../types/__generated__';
import config from '../../client/config';
import AboutCampaignAndCharitySection from '../../components/AboutCampaignAndCharitySection/AboutCampaignAndCharitySection';
import AboutCauseMini from '../../components/AboutCauseMini/AboutCauseMini';
import { Banner } from '../../components/Banner/Banner';
import { DonateToYourselfModal } from '../../components/DonateToYourselfModal/DonateToYourselfModal';
import DonationSummary, {
  DonationField,
} from '../../components/DonationSummary/DonationSummary';
import EventDetails from '../../components/EventDetails/EventDetails';
import FeatureImage from '../../components/FeatureImage/FeatureImage';
import { FitbitManager } from '../../components/FitnessManager/FitbitManager';
import { StravaManager } from '../../components/FitnessManager/StravaManager';
import FundraisingCTA from '../../components/FundraisingCTA/FundraisingCTA';
import FundraisingHelmet from '../../components/Helmet/FundraisingHelmet';
import IsPageOwner from '../../components/IsPageOwner/IsPageOwner';
import IsQRCode from '../../components/IsQRCode/IsQRCode';
import DefaultLayout from '../../components/Layout/DefaultLayout';
import Page from '../../components/Page/Page';
import PageAlert from '../../components/PageAlert/PageAlert';
import { PageOwnerAvatar } from '../../components/PageOwnerAvatar/PageOwnerAvatar';
import PageOwnerTitle from '../../components/PageOwnerTitle/PageOwnerTitle';
import PageSection from '../../components/PageSection/PageSection';
import Reason from '../../components/Reason/Reason';
import { ShareModal } from '../../components/ShareModal/ShareModal';
import { FundraisingShareProvider } from '../../components/SocialButtons/FundraisingShareProvider';
import SocialButtonsList from '../../components/SocialButtons/SocialButtonsList';
import Story from '../../components/Story/Story';
import SupportersList from '../../components/SupportersList/SupportersList';
import ESSupportersList from '../../components/ESSupportersList/ESSupportersList';
import TeamDetails from '../../components/TeamDetails/TeamDetails';
import TotaliserWrapper from '../../components/TotaliserWrapper/TotaliserWrapper';
import { FirstToDonateTotals } from '../../components/Totals/FirstToDonateTotals';
import { Totals } from '../../components/Totals/Totals';
import VideoStoryCheckList from '../../components/VideoStoryCheckList/VideoStoryCheckList';
import { MODAL } from '../../context';
import {
  buildQuery,
  useApp,
  useBasePageQuery,
  useFlags,
  useOptimizely,
  usePageMeta,
  usePageOwner,
  usePageQuery,
} from '../../hooks';
import { useOpenDonateToYourselfModal } from '../../hooks/fundraising/useOpenDonateToYourselfModal';
import { useInView } from '../../hooks/useinView';
import { useScript } from '../../hooks/useScript';
import { useShouldDisplayFirstToDonateTotals } from '../../hooks/useShouldDisplayFirstToDonateTotals';
import Fragment from './FundraisingPageFragment.gql';

import styles from './FundraisingPage.scss';
import { NativeShareModal } from '../../components/NativeShareModal/NativeShareModal';
import { CheckoutModal } from '../../components/CheckoutModal/CheckoutModal';
import { PopupBlockedModal } from '../../components/PopupBlockedModal/PopupBlockedModal';
import { PopupRefocusModal } from '../../components/PopupRefocusModal/PopupRefocusModal';
import { PageTopBar } from '../../components/PageTopBar/PageTopBar';
import { ShareModalPlatforms } from '../../components/NativeShareModal/NativeShareEnums';
import VerifiedBanner from '../../components/VerifiedBanner/VerifiedBanner';
import { Experiments } from '../../constants/experiments';

const Prompt = loadable(() => import('../../components/Prompt/Prompt'));
const QRModal = loadable(() => import('../../components/QRModal/QRModal'));
const Editbar = loadable(() => import('../../components/Editbar/Editbar'));
const TimelinePageSection = loadable(() =>
  import('../../components/Timeline/TimelinePageSection/TimelinePageSection'),
);
const TeamAndCompany = loadable(() =>
  import('../../components/TeamAndCompany/TeamAndCompany'),
);
const QualarooLoader = loadable(
  () => import('../../components/QualarooLoader/QualarooLoader'),
  { resolveComponent: module => module.QualarooLoader },
);
const StickySummary = loadable(() =>
  import('../../components/StickySummary/StickySummary'),
);
const EncourageInactivityModal = loadable(() =>
  import('../../components/EncourageInactivityModal/EncourageInactivityModal'),
);

const FundraisingPageQuery = buildQuery(Fragment);

export const FundraisingPage: React.FunctionComponent = () => {
  useScript('//rum-static.pingdom.net/pa-5e3af8dc229e9300080003d1.js', {
    async: true,
  });

  const { inViewRef, inView, rootIsBelowTarget } = useInView();
  const { data: basePageData } = useBasePageQuery();
  const { data } = usePageQuery<FundraisingPageFragment>(FundraisingPageQuery);
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageShortName } = usePageMeta();
  const optimizely = useOptimizely();
  const {
    useOTGVideoGenerator,
    enableFitbit,
    enableSupportersService,
  } = useFlags();
  useOpenDonateToYourselfModal({
    totalDonationAmount:
      basePageData?.page?.donationSummary?.totalAmount?.value,
  });

  const decision = optimizely?.decide(
    Experiments.ConsumerDarkslide.VerifiedCharity.flagKey,
  );

  const shouldDisplayFirstToDonateTotals = useShouldDisplayFirstToDonateTotals();

  const name = data?.owner?.name ?? '';
  const eventGivingGroupId = data?.eventGivingGroupId;
  const totalRelatedCompanies = data?.relationships?.companies.totalCount ?? 0;

  const displayTeamAndCompany =
    totalRelatedCompanies > 0 ||
    data?.relationships?.teams.nodes[0]?.status === PageStatus.Active;

  const adminLink = `${config.baseUri}/account/your-pages/${pageShortName}?pageId=${eventGivingGroupId}`;
  const fundraisingPageUrl = `${config.baseUri}/${pageShortName}`;

  const { hideModal, openModal } = useApp();
  const { isPageOwner } = usePageOwner();

  return (
    <FundraisingShareProvider>
      <Page>
        {openModal === MODAL.DONATE_TO_YOURSELF && <DonateToYourselfModal />}
        {openModal === MODAL.SHARE && (
          <ShareModal
            heading={
              isPageOwner
                ? t('shareModal.heading.pageOwner')
                : t('shareModal.heading', {
                    pageOwner: data?.owner?.name,
                  })
            }
            description={t('shareModal.description')}
            onClose={hideModal}
          />
        )}
        {openModal === MODAL.INSTAGRAM_SHARE && (
          <NativeShareModal
            onClose={hideModal}
            buttonOptions={[
              { label: 'Instagram Post', value: 'Instagram Post' },
              { label: 'Instagram Story', value: 'Instagram Story' },
            ]}
            platform={ShareModalPlatforms.INSTAGRAM}
          />
        )}
        {openModal === MODAL.SNAPCHAT_SHARE && (
          <NativeShareModal
            onClose={hideModal}
            platform={ShareModalPlatforms.SNAPCHAT}
          />
        )}
        {openModal === MODAL.CHECKOUT && <CheckoutModal onClose={hideModal} />}
        {openModal === MODAL.POPUP_BLOCKED && (
          <PopupBlockedModal onClose={hideModal} />
        )}
        {openModal === MODAL.POPUP_REFOCUS && (
          <PopupRefocusModal onClose={hideModal} />
        )}
        <IsQRCode>
          <QRModal ownerName={name} />
        </IsQRCode>
        <PageAlert />
        <Banner />
        {eventGivingGroupId && (
          <IsPageOwner>
            <Prompt
              entityId={eventGivingGroupId.toString()}
              url={fundraisingPageUrl}
            />
          </IsPageOwner>
        )}
        <PageTopBar>
          <Editbar adminLink={adminLink} />
        </PageTopBar>

        <FundraisingHelmet />
        <DefaultLayout
          slots={{
            featureImage: (
              <FeatureImage enableLightbox imageObjectFit="contain" />
            ),
            ownerAvatar: <PageOwnerAvatar />,
            titles: <PageOwnerTitle />,
            totals: (
              <>
                <TotaliserWrapper />
                {shouldDisplayFirstToDonateTotals ? (
                  <FirstToDonateTotals />
                ) : (
                  <Totals />
                )}
              </>
            ),
            callsToAction: (
              <div ref={inViewRef} className={styles.ctaWrapper}>
                <FundraisingCTA />
              </div>
            ),
            titlesSupplementary: (
              <div className="jg-space-mbxs jg-space-mbsm@xl jg-space-mbn jg-space-mtxs jg-space-mtsm@xl">
                <EventDetails />
                <Reason />
                <TeamDetails />
              </div>
            ),
            causeMini: <AboutCauseMini />,
            main: (
              <>
                <PageSection
                  title={t('story.title')}
                  className="jg-pages-fundraising__story"
                  hasBorder={true}
                >
                  <Story />
                  <h2 className="jg-h3 jg-text--center">
                    {t('story.shareTitle', 'Share this story')}
                  </h2>
                  <SocialButtonsList />
                </PageSection>
                <TimelinePageSection />
              </>
            ),
            aside: (
              <>
                {useOTGVideoGenerator && (
                  <IsPageOwner>
                    <PageSection>
                      <VideoStoryCheckList />
                    </PageSection>
                  </IsPageOwner>
                )}
                <PageSection>
                  <StravaManager />
                </PageSection>
                {enableFitbit && (
                  <PageSection>
                    <FitbitManager />
                  </PageSection>
                )}
                {enableSupportersService ? (
                  <ESSupportersList
                    noSupportersMessage={t(
                      'fundraising.noSupportersMessage',
                      `Become {{name}}'s first supporter by making a donation`,

                      { replace: { name } },
                    )}
                    displayDonateButton
                  />
                ) : (
                  <SupportersList
                    noSupportersMessage={t(
                      'fundraising.noSupportersMessage',
                      `Become {{name}}'s first supporter by making a donation`,

                      { replace: { name } },
                    )}
                    displayDonateButton
                  />
                )}
              </>
            ),
            teamAndCompany: displayTeamAndCompany && <TeamAndCompany />,
            contentFooterPrimary: <AboutCampaignAndCharitySection />,
            contentFooterSecondary: (
              <DonationSummary
                fieldsToShow={[
                  DonationField.OnlineAmount,
                  DonationField.OfflineAmount,
                  DonationField.TextDonations,
                ]}
              />
            ),
          }}
        />
        {decision?.enabled && <VerifiedBanner />}
        {!inView && rootIsBelowTarget && <StickySummary />}
        <EncourageInactivityModal />
        <QualarooLoader />
      </Page>
    </FundraisingShareProvider>
  );
};

FundraisingPage.displayName = 'FundraisingPage';

export default FundraisingPage;
